
import React, { useEffect, useRef, useState } from 'react';
import { Users, ShoppingCart, Clock } from 'lucide-react';

const MissionStats = () => {
  const [isVisible, setIsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  const stats = [
    {
      icon: Users,
      number: 1500,
      suffix: '+',
      label: 'Adaptive Athletes Supported',
      description: 'Empowering athletes of all abilities'
    },
    {
      icon: ShoppingCart,
      number: 10000,
      suffix: '+',
      label: 'Pieces Sold',
      description: 'Quality gear reaching every court'
    },
    {
      icon: Clock,
      number: 5000,
      suffix: '+',
      label: 'Community Hours',
      description: 'Building stronger communities together'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const CountUpNumber = ({ targetNumber, suffix = '', duration = 2000 }: { targetNumber: number; suffix?: string; duration?: number }) => {
    const [currentNumber, setCurrentNumber] = useState(0);

    useEffect(() => {
      if (!isVisible) return;

      let startTime: number;
      const step = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCurrentNumber(Math.floor(progress * targetNumber));
        
        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };
      
      requestAnimationFrame(step);
    }, [isVisible, targetNumber, duration]);

    return <span>{currentNumber.toLocaleString()}{suffix}</span>;
  };

  return (
    <section className="py-12 bg-lime-400">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-6xl font-black text-black mb-4 tracking-tight">
            OUR IMPACT
          </h2>
          <p className="text-xl text-black/80 max-w-2xl mx-auto">
            Every purchase drives our mission forward. Here's how we're changing the game together.
          </p>
        </div>

        {/* Stats Grid */}
        <div ref={statsRef} className="grid md:grid-cols-3 gap-8">
          {stats.map((stat, index) => (
            <div 
              key={index}
              className="text-center group hover:scale-105 transition-transform duration-300"
              style={{ animationDelay: `${index * 200}ms` }}
            >
              <div className="bg-black rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300">
                {/* Icon */}
                <div className="inline-flex items-center justify-center w-16 h-16 bg-lime-400 rounded-full mb-6 group-hover:scale-110 transition-transform duration-300">
                  <stat.icon className="w-8 h-8 text-black" />
                </div>

                {/* Number */}
                <div className="text-4xl lg:text-5xl font-black text-lime-400 mb-2">
                  <CountUpNumber targetNumber={stat.number} suffix={stat.suffix} />
                </div>

                {/* Label */}
                <h3 className="text-xl font-bold text-white mb-2 tracking-wide">
                  {stat.label}
                </h3>

                {/* Description */}
                <p className="text-gray-300 leading-relaxed">
                  {stat.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Mission Statement */}
        <div className="mt-16 text-center">
          <div className="bg-black rounded-2xl p-8 lg:p-12 max-w-4xl mx-auto">
            <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
              "Sports shouldn't have barriers. Style shouldn't have limits."
            </h3>
            <p className="text-gray-300 text-lg leading-relaxed">
              Founded by adaptive athletes, for adaptive athletes. PBX Pickle champions inclusion on and off the court, 
              creating gear that performs as boldly as the people who wear it.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MissionStats;
